import React, { useState, useEffect, useRef } from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';
import logoImg from '../assets/WealthSpan-cropped.png';

const BASE_TABS = [
  { view: 'budget',    label: 'Budget'      },
  { view: 'calendar',  label: 'Calendar'    },
  { view: 'forecast',  friendlyLabel: 'Real Balance', formalLabel: 'Forecast' },
  { view: 'debt',      label: 'Debt'        },
  { view: 'goals',     label: 'Goals'       },
  { view: 'spending',  label: 'Spending'    },
  { view: 'dashboard', label: 'Dashboard'   },
  { view: 'settings',  label: 'Settings',   mobileOnly: true },
];

export default function NavBar({ currentView, onNavigate, rightSlot, isPro }) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';
  const { t, friendlyMode, isDark } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const TABS = BASE_TABS.map(tab =>
    tab.friendlyLabel
      ? { ...tab, label: friendlyMode ? tab.friendlyLabel : tab.formalLabel }
      : tab
  );

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Close menu on view change
  useEffect(() => { setMenuOpen(false); }, [currentView]);

  const navHeight = isDesktop ? '56px' : '52px';
  const navPadding = isDesktop ? '0 20px' : isTablet ? '0 16px' : '0 12px';
  const tabPadding = isTablet ? '7px 10px' : '8px 16px';
  const tabFontSize = isTablet ? '13px' : '14px';
  const brandFontSize = isTablet ? '16px' : '20px';
  // Tab group: fixed left margin from logo, auto right to push rightSlot far-right
  const tabsMarginLeft = isDesktop ? '32px' : '24px';

  const handleNavigate = (view) => {
    onNavigate(view);
    setMenuOpen(false);
  };

  return (
    <>
      <nav
        ref={menuRef}
        style={{
          background: t.nav,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${t.navBorder}`,
          padding: navPadding,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          height: navHeight,
          gap: '4px',
        }}>

          {/* Logo — desktop & tablet */}
          {!isMobile && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
            }}>
              <img
                src={logoImg}
                alt="WealthSpan"
                style={{ height: isTablet ? '28px' : '32px', width: 'auto', display: 'block' }}
              />
              <span style={{
                fontWeight: '800',
                color: t.text,
                whiteSpace: 'nowrap',
                letterSpacing: '-0.02em',
                fontSize: brandFontSize,
              }}>
                WealthSpan
              </span>
            </div>
          )}

          {/* Mobile: logo (small) + hamburger */}
          {isMobile && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                <img src={logoImg} alt="WealthSpan" style={{ height: '26px', width: 'auto' }} />
                <span style={{ fontWeight: '800', color: t.text, fontSize: '16px', letterSpacing: '-0.02em' }}>
                  WealthSpan
                </span>
              </div>
              <button
                onClick={() => setMenuOpen(v => !v)}
                aria-label="Open navigation menu"
                style={{
                  background: menuOpen ? t.borderLight : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  width: '38px',
                  height: '38px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  padding: '8px',
                  flexShrink: 0,
                  transition: 'background 0.15s',
                }}
              >
                <span style={{
                  display: 'block', width: '18px', height: '2px',
                  background: menuOpen ? t.navActive : t.textMuted,
                  borderRadius: '2px',
                  transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none',
                  transition: 'transform 0.2s, background 0.15s',
                }} />
                <span style={{
                  display: 'block', width: '18px', height: '2px',
                  background: menuOpen ? 'transparent' : t.textMuted,
                  borderRadius: '2px',
                  opacity: menuOpen ? 0 : 1,
                  transition: 'opacity 0.15s',
                }} />
                <span style={{
                  display: 'block', width: '18px', height: '2px',
                  background: menuOpen ? t.navActive : t.textMuted,
                  borderRadius: '2px',
                  transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none',
                  transition: 'transform 0.2s, background 0.15s',
                }} />
              </button>
            </>
          )}

          {/* Tab bar — tablet & desktop only */}
          {!isMobile && (
            <div
              className="nav-tabs"
              style={{
                display: 'flex',
                gap: isDesktop ? '10px' : '2px',
                overflowX: 'auto',
                alignItems: 'center',
                height: '100%',
                justifyContent: 'flex-start',
                marginLeft: tabsMarginLeft,
                marginRight: 'auto',
              }}
            >
              {TABS.filter(tab => !tab.mobileOnly).map(tab => {
                const active = currentView === tab.view;
                const isSpending = tab.view === 'spending';
                return (
                  <button
                    key={tab.view}
                    onClick={() => handleNavigate(tab.view)}
                    style={{
                      background: 'transparent',
                      color: active ? t.navActive : t.textMuted,
                      border: 'none',
                      borderBottom: active ? `2px solid ${t.navActive}` : '2px solid transparent',
                      borderRadius: '0',
                      padding: tabPadding,
                      paddingBottom: active ? 'calc(8px - 2px)' : '8px',
                      marginBottom: '-1px',
                      cursor: 'pointer',
                      fontSize: tabFontSize,
                      fontWeight: active ? '600' : '400',
                      fontFamily: 'inherit',
                      whiteSpace: 'nowrap',
                      transition: 'color 0.15s, border-color 0.15s',
                      height: '100%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {tab.label}
                    {isSpending && !isPro && (
                      <span style={{
                        fontSize: '9px', fontWeight: '700', padding: '1px 5px',
                        background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
                        color: 'white', borderRadius: '8px', letterSpacing: '0.03em',
                      }}>PRO</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Right slot */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {rightSlot}
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {isMobile && menuOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 99,
            background: t.card,
            borderBottom: `1px solid ${t.navBorder}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}>
            {TABS.map((tab, i) => {
              const active = currentView === tab.view;
              const isSpending = tab.view === 'spending';
              return (
                <button
                  key={tab.view}
                  onClick={() => handleNavigate(tab.view)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: '14px 20px',
                    background: active
                      ? (isDark ? 'rgba(0,97,164,0.12)' : 'rgba(0,97,164,0.06)')
                      : 'transparent',
                    border: 'none',
                    borderTop: i > 0 ? `1px solid ${t.borderLight}` : 'none',
                    borderLeft: active ? `3px solid ${t.navActive}` : '3px solid transparent',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '15px',
                    fontWeight: active ? '600' : '400',
                    color: active ? t.navActive : t.text,
                    textAlign: 'left',
                    gap: '10px',
                    transition: 'background 0.1s',
                    boxSizing: 'border-box',
                  }}
                >
                  <span style={{ flex: 1 }}>{tab.label}</span>
                  {isSpending && !isPro && (
                    <span style={{
                      fontSize: '9px', fontWeight: '700', padding: '2px 6px',
                      background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
                      color: 'white', borderRadius: '8px', letterSpacing: '0.03em',
                    }}>PRO</span>
                  )}
                  {active && (
                    <span style={{ fontSize: '12px', color: t.navActive, opacity: 0.7 }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* Backdrop for mobile menu */}
      {isMobile && menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 98,
            background: 'rgba(0,0,0,0.2)',
          }}
        />
      )}
    </>
  );
}
